import type { IMediaTreeItem } from "../models/Interfaces";

export interface MediaTreeState {
  items: IMediaTreeItem[] | undefined;
  refreshing: boolean;
  error: string | null;
  updatedAt: number;
}
export const emptyMediaTreeState: MediaTreeState = { items: undefined, refreshing: false, error: null, updatedAt: 0 };
const MAX_AGE = 5 * 60 * 1000;
type Loader = () => Promise<IMediaTreeItem[]>;
interface Entry {
  state: MediaTreeState;
  listeners: Set<() => void>;
  loader?: Loader;
  pending?: Promise<IMediaTreeItem[]>;
  timer?: ReturnType<typeof setTimeout>;
  revision: number;
}

// One in-memory tree per user/site, shared by services, React pickers and editor frames.
export class MediaTreeCache {
  private entries = new Map<string, Entry>();
  private entry(key: string): Entry {
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { state: emptyMediaTreeState, listeners: new Set(), revision: 0 };
      this.entries.set(key, entry);
    }
    return entry;
  }
  snapshot(key: string): MediaTreeState { return this.entries.get(key)?.state ?? emptyMediaTreeState; }
  private publish(entry: Entry, state: MediaTreeState) {
    entry.state = state;
    entry.listeners.forEach(listener => listener());
  }
  subscribe(key: string, listener: () => void) {
    const entry = this.entry(key);
    entry.listeners.add(listener);
    this.schedule(key, entry);
    return () => {
      entry.listeners.delete(listener);
      if (!entry.listeners.size) clearTimeout(entry.timer);
    };
  }
  private schedule(key: string, entry: Entry) {
    clearTimeout(entry.timer);
    if (!entry.listeners.size || !entry.loader || !entry.state.updatedAt || entry.state.error) return;
    entry.timer = setTimeout(() => { void this.refresh(key, entry.loader!).catch(() => {}); },
      Math.max(0, entry.state.updatedAt + MAX_AGE - Date.now()));
  }
  load(key: string, loader: Loader): Promise<IMediaTreeItem[]> {
    const entry = this.entry(key);
    entry.loader = loader;
    if (entry.state.items !== undefined) {
      if (Date.now() - entry.state.updatedAt >= MAX_AGE) void this.refresh(key, loader).catch(() => {});
      return Promise.resolve(entry.state.items);
    }
    return this.refresh(key, loader);
  }
  refresh(key: string, loader: Loader): Promise<IMediaTreeItem[]> {
    const entry = this.entry(key);
    entry.loader = loader;
    if (entry.pending) return entry.pending;
    clearTimeout(entry.timer);
    // Start on a microtask so every subscriber sees the same in-flight promise.
    const pending = Promise.resolve().then(async () => {
      try {
        let items: IMediaTreeItem[];
        let revision: number;
        do {
          revision = entry.revision;
          items = await entry.loader!();
          if (this.entries.get(key) !== entry) return [];
          // An upload/deletion during a fetch requires another fetch; never publish the old list.
        } while (revision !== entry.revision);
        this.publish(entry, { items, refreshing: false, error: null, updatedAt: Date.now() });
        return items;
      } catch (error) {
        if (this.entries.get(key) === entry) this.publish(entry, { ...entry.state, refreshing: false,
          error: "Could not refresh media. Your existing list is still available; please retry." });
        throw error;
      } finally {
        if (entry.pending === pending) entry.pending = undefined;
        if (this.entries.get(key) === entry) this.schedule(key, entry);
      }
    });
    entry.pending = pending;
    this.publish(entry, { ...entry.state, refreshing: true, error: null });
    return pending;
  }
  invalidate(key: string) {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.revision++;
    this.publish(entry, { ...entry.state, updatedAt: 0 });
    if (entry.listeners.size && entry.loader) void this.refresh(key, entry.loader).catch(() => {});
  }
  clear() {
    const entries = [...this.entries.values()];
    this.entries.clear();
    entries.forEach(entry => { clearTimeout(entry.timer); entry.listeners.forEach(listener => listener()); });
  }
}
export const mediaTreeCache = new MediaTreeCache();
if (typeof window !== "undefined") window.addEventListener("cms-session-ended", () => mediaTreeCache.clear());
